import { type FunctionComponent, useEffect, useState } from 'react';
import { configureRoute } from '../../../utils/routes/configure-route';
import { CreateAccountStart } from '../../components/account-components/create-account-start/create-account-start.component';
import { InternalLoginForm } from '../../components/account-components/internal-login-form/internal-login-form.component';
import { LoginForm } from '../../components/account-components/login-form/login-form.component';
import { RequestPasswordResetForm } from '../../components/account-components/request-password-reset-form/request-password-reset-form.component';
import { PageContainer } from '../../components/common-components/page/page-container/page-container.component';
import { AUTH_REDIRECT_REASON } from '../../constants/general';
import { ACCOUNT_CREATE_LINK, ACCOUNT_LOGIN_LINK } from '../../constants/links';
import { LOGIN_PAGE_TITLE_META, LOGN_PAGE_DESCRIPTION_META } from '../../constants/meta';
import { isValidReturnTo } from '../../helpers/login/login.helpers';
import { useTrackPageView } from '../../hooks/analytics/analytics.hooks';
import { useEmployee } from '../../hooks/apollo/employee/employee.hooks';
import { useDyRecommendationContext, DyRecommendationContextType } from '../../hooks/dynamic-yield/dynamic-yield-client.hooks';
import { useNavigation } from '../../hooks/navigation/navigation.hooks';
import { useNotification } from '../../hooks/notification/notification.hooks';
import { useRouterLocation } from '../../hooks/routing/location.hook';
import { type LoginModes } from '../../types/authentication.types';

export const Login: FunctionComponent = () => {
	useTrackPageView({ pageName: 'account:login' });
	const [mode, setMode] = useState<LoginModes>('login');
	const navigate = useNavigation();
	const { notifyWarning } = useNotification();
	const { employee } = useEmployee();
	const { search } = useRouterLocation();

	const searchParams = new URLSearchParams(search);
	const error = searchParams.get('error');

	useEffect(() => {
		if (error === AUTH_REDIRECT_REASON.SESSION_EXPIRED) {
			notifyWarning('Your session has expired. Please log in again.', 0);
		}
	}, [error]);

	useDyRecommendationContext({ type: DyRecommendationContextType.OTHER_PAGE });

	const isInternal = employee.isAuthenticated;

	const onLoginSuccess = () => {
		const returnTo = searchParams.get('returnto');
		if (isValidReturnTo(returnTo)) {
			navigate(returnTo);
		} else {
			navigate(-1);
		}
	};

	return (
		<PageContainer canonicalURL={ACCOUNT_LOGIN_LINK} pageTitle={LOGIN_PAGE_TITLE_META} metaDescription={LOGN_PAGE_DESCRIPTION_META}>
			<>
				{mode === 'reset_password' && (
					<div className="w-100 w-50-ns center">
						<RequestPasswordResetForm onBack={() => setMode('login')} />
					</div>
				)}
				{mode === 'login' && (
					<>
						<div className="flex flex-wrap flex-nowrap-ns">
							<div className="w-100 w-50-ns">
								<LoginForm onLoginSuccess={onLoginSuccess} onPasswordReset={() => setMode('reset_password')} />
							</div>
							<div className="w-100 w-50-ns mt2 mt0-ns ml3-ns">
								<CreateAccountStart onCreateAccount={() => navigate(ACCOUNT_CREATE_LINK)} />
							</div>
						</div>
						{isInternal && (
							<div className="w-100 w-50-ns mt2 mt3 pr2">
								<InternalLoginForm onLoginSuccess={onLoginSuccess} />
							</div>
						)}
					</>
				)}
			</>
		</PageContainer>
	);
};

export const RoutedLogin = configureRoute({ path: ACCOUNT_LOGIN_LINK }, Login);
